import { unescape } from 'lodash-es';
import { filterXSS, getDefaultWhiteList } from 'xss';

const SAFE_WHITELIST = { ...getDefaultWhiteList() };
(['script', 'iframe', 'object', 'embed', 'form', 'base'] as const).forEach(tag => delete SAFE_WHITELIST[tag]);

export const sanitizeHtmlContent = (html: string): string => filterXSS(html, { whiteList: SAFE_WHITELIST, stripIgnoreTag: true });

/**
 * Reduces HTML to clean plain text. Used for plain-text consumers such as Open Graph /
 * meta description tags, where leftover markup or entity artifacts look broken in link previews.
 *
 * Implemented isomorphically (no DOM) so it works during SSR, unlike the textarea-based
 * `decodeHtmlEntities` in `@shared/utils/html.ts` which returns its input unchanged on the server:
 *   1. `unescape` resolves the basic HTML entities (`&amp;` → `&`, `&lt;`/`&gt;`, `&quot;`, `&#39;`)
 *      and unwraps double-escaped markup (`&lt;p&gt;` → `<p>`) so real tags are exposed next.
 *   2. `filterXSS` with `stripIgnoreTag` removes the tags entirely (rather than escaping them,
 *      which is what an empty whitelist alone does), leaving only the text content.
 *   3. `unescape` again resolves anything `filterXSS` re-encoded while stripping (e.g. a stray
 *      `&` becomes `&amp;`).
 *
 * Only the basic entity set is decoded — enough to keep the common artifacts (notably `&amp;`,
 * e.g. "Bar &amp; Grill") out of previews without pulling in a full entity-table dependency.
 * Less common named entities (`&mdash;`, `&nbsp;`, accents) pass through unchanged.
 */
export const stripHtmlTags = (html: string): string => unescape(filterXSS(unescape(html), { whiteList: {}, stripIgnoreTag: true }));

// Like `sanitizeHtmlContent`, but also keeps the inline `style` attribute the rich-text editor
// emits — restricted to `text-align` so the align button survives the round-trip. Every other CSS
// property (and all the XSS vectors `sanitizeHtmlContent` blocks) is still stripped.
const RICH_TEXT_WHITELIST: Record<string, string[]> = {};
Object.keys(SAFE_WHITELIST).forEach(tag => {
  const list = SAFE_WHITELIST[tag] ?? [];
  RICH_TEXT_WHITELIST[tag] = list.includes('style') ? list : [...list, 'style'];
});

export const sanitizeRichTextHtml = (html: string): string => filterXSS(html, { whiteList: RICH_TEXT_WHITELIST, stripIgnoreTag: true, css: { whiteList: { 'text-align': true } } });

// Website-header event title (eventName.contentHtml): the minimal tag set the title editor emits,
// plus the two style props its per-line font controls write. Everything else — other CSS (color,
// position…), every other tag/attribute, and all XSS vectors — is stripped. <font> is deliberately
// not whitelisted, so pasted <font> tags reduce to their text.
const EVENT_TITLE_WHITELIST: Record<string, string[]> = {
  p: ['style'],
  div: ['style'],
  br: [],
  span: ['style'],
  strong: [],
  em: [],
  u: [],
};

export const sanitizeEventTitleHtml = (html: string): string =>
  filterXSS(html, { whiteList: EVENT_TITLE_WHITELIST, stripIgnoreTag: true, css: { whiteList: { 'font-family': true, 'font-size': true } } });
